/**
 * Page publique /prototype/:conceptId (Phase 7).
 *
 * - Charge le prototype existant via GET /api/prototype/{concept_id}
 * - Si 404 → tente une génération (POST) — utile quand un visiteur atterrit sur une
 *   URL de showcase encore jamais visitée.
 * - Affiche le PrototypeViewer + les métadonnées (nom, pitch, badge verticale)
 *   + CTA teaser vers /tarifs
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { PrototypeViewer } from '@/components/prototype/PrototypeViewer';
import { getPrototype, generatePrototype } from '@/lib/api';
import { createApp } from '@/lib/apps-api';
import { useAuth } from '@/lib/auth';
import { track } from '@/lib/analytics';
import { setPageMeta } from '@/lib/seo';
import { useT, useLang, useRaw } from '@/lib/i18n';
import { toast } from 'sonner';
import { Loader2, Sparkles, ArrowRight, LayoutTemplate } from 'lucide-react';

const LOADING_STEPS_MS = 3500;

export default function Prototype() {
  const t = useT();
  const raw = useRaw();
  const { lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { conceptId } = useParams();
  const [prototype, setPrototype] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | locked | not_found | error
  const [stepIndex, setStepIndex] = useState(0);
  const [openingBuilder, setOpeningBuilder] = useState(false);
  const loadingSteps = raw('prototype.loading_steps') || [];

  useEffect(() => {
    setPageMeta({
      title: t('seo.prototype_title'),
      description: t('seo.prototype_description'),
      path: `/prototype/${conceptId}`,
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang, conceptId]);

  useEffect(() => {
    let cancelled = false;
    let stepsTimer = null;

    const load = async () => {
      if (!conceptId) return;
      setStatus('loading');
      setStepIndex(0);

      // Boucle animée de messages de chargement
      const cycleSteps = () => {
        if (cancelled) return;
        setStepIndex((i) => Math.min(loadingSteps.length - 1, i + 1));
        stepsTimer = setTimeout(cycleSteps, LOADING_STEPS_MS);
      };
      stepsTimer = setTimeout(cycleSteps, LOADING_STEPS_MS);

      // 1) tenter GET cache
      let res = await getPrototype(conceptId);
      // 2) si pas encore existant → on demande la génération
      if (!res.ok && res.code === 'prototype_not_found') {
        res = await generatePrototype(conceptId);
        if (res.ok) {
          track('prototype_generated', { lang, concept_id: conceptId });
        }
      }
      if (cancelled) return;

      if (res.ok) {
        setPrototype(res.prototype);
        setStatus('ready');
        track('prototype_viewed', { lang, concept_id: conceptId });
      } else if (res.code === 'prototype_locked') {
        setStatus('locked');
      } else if (res.code === 'prototype_not_found') {
        setStatus('not_found');
      } else {
        setStatus('error');
      }
    };

    load();
    return () => {
      cancelled = true;
      if (stepsTimer) clearTimeout(stepsTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptId]);

  const verticalCode = prototype?.theme?.vertical;
  const accent = prototype?.theme?.accent || '#0A0E1A';
  const verticalLabel = verticalCode ? t(`verticals.${verticalCode}.short`) : '';

  return (
    <div data-testid="page-prototype">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-24">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute left-1/2 -top-16 h-[420px] w-[420px] -translate-x-1/2 rounded-full blur-3xl"
              style={{ background: `radial-gradient(closest-side, ${accent}33, transparent 70%)` }}
            />
          </div>

          <div className="om-container">
            {status === 'loading' && (
              <div className="mx-auto max-w-xl text-center" data-testid="prototype-loading">
                <div className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-full bg-[var(--om-ink)]/5">
                  <Loader2 size={28} strokeWidth={2.4} className="animate-spin text-[var(--om-ink)]" />
                </div>
                <span className="om-eyebrow">{t('prototype.loading_eyebrow')}</span>
                <h1 className="om-h1 mt-4 text-[28px] sm:text-[36px] text-[var(--om-ink)]">
                  {t('prototype.loading_title')}
                </h1>
                <div className="mt-6 min-h-[24px] text-[14px] font-medium text-[var(--om-muted)]">
                  {loadingSteps[stepIndex] || loadingSteps[0]}
                </div>
              </div>
            )}

            {status === 'ready' && prototype && (
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-5">
                  {verticalLabel && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: `${accent}18`, color: accent }}
                      data-testid="prototype-vertical-badge"
                    >
                      <Sparkles size={11} strokeWidth={2.6} /> {verticalLabel}
                    </span>
                  )}
                  <h1
                    className="mt-4 text-[36px] sm:text-[44px] lg:text-[52px] font-extrabold leading-[1.05] tracking-tight text-[var(--om-ink)]"
                    data-testid="prototype-app-name"
                  >
                    {prototype.app_name}
                  </h1>
                  <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-[var(--om-muted)]" data-testid="prototype-pitch">
                    {prototype.pitch}
                  </p>

                  <div className="mt-8 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5" data-testid="prototype-cta-teaser">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
                      {t('prototype.teaser_eyebrow')}
                    </div>
                    <h2 className="mt-1 text-[17px] font-extrabold text-[var(--om-ink)]">
                      {t('prototype.teaser_title')}
                    </h2>
                    <p className="mt-2 text-[13px] leading-relaxed text-[var(--om-muted)]">
                      {t('prototype.teaser_subtitle')}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          if (openingBuilder || authLoading) return;
                          if (!user) {
                            toast(t('builder.open_in_builder_needs_login'), { icon: '🔒' });
                            navigate(`/connexion?next=${encodeURIComponent(`/prototype/${conceptId}`)}`);
                            return;
                          }
                          if (!user.plan || user.plan === 'none') {
                            toast(t('builder.open_in_builder_needs_plan'), { icon: '🔒' });
                            navigate('/tarifs');
                            return;
                          }
                          setOpeningBuilder(true);
                          const res = await createApp({ from_concept_id: conceptId, lang: prototype?.lang });
                          setOpeningBuilder(false);
                          if (res.ok) {
                            track('app_created', { lang, vertical: prototype?.vertical });
                            navigate(`/studio/app/${res.app.id}`);
                          } else if (res.status === 403) {
                            toast.error(t('builder.errors.invalid'));
                          } else {
                            toast.error(t('builder.errors.create_failed'));
                          }
                        }}
                        disabled={openingBuilder}
                        className="om-btn-primary disabled:opacity-60"
                        data-testid="prototype-open-builder-btn"
                      >
                        <LayoutTemplate size={13} strokeWidth={2.4} />
                        {openingBuilder ? t('builder.opening') : t('builder.open_in_builder')}
                        <ArrowRight size={13} strokeWidth={2.4} />
                      </button>
                      <Link
                        to="/tarifs"
                        className="om-btn-ghost"
                        data-testid="prototype-cta-pricing"
                      >
                        {t('prototype.teaser_cta')}
                      </Link>
                    </div>
                  </div>

                  <p className="mt-6 text-[11px] leading-relaxed text-[var(--om-muted-2)]">
                    {t('prototype.disclaimer')}
                  </p>
                </div>

                <div className="lg:col-span-7">
                  <PrototypeViewer prototype={prototype} />
                </div>
              </div>
            )}

            {status === 'locked' && (
              <div className="mx-auto max-w-xl text-center" data-testid="prototype-locked">
                <h1 className="om-h1 text-[28px] sm:text-[36px] text-[var(--om-ink)]">
                  {t('prototype.locked_title')}
                </h1>
                <p className="mt-4 text-[15px] leading-relaxed text-[var(--om-muted)]">
                  {t('prototype.locked_subtitle')}
                </p>
                <div className="mt-6 flex justify-center">
                  <Link to="/generateur" className="om-btn-primary" data-testid="prototype-locked-cta">
                    {t('prototype.locked_cta')} <ArrowRight size={16} strokeWidth={2.4} />
                  </Link>
                </div>
              </div>
            )}

            {status === 'not_found' && (
              <div className="mx-auto max-w-xl text-center" data-testid="prototype-notfound">
                <h1 className="om-h1 text-[28px] sm:text-[36px] text-[var(--om-ink)]">
                  {t('prototype.notfound_title')}
                </h1>
                <p className="mt-4 text-[15px] leading-relaxed text-[var(--om-muted)]">
                  {t('prototype.notfound_subtitle')}
                </p>
                <div className="mt-6 flex justify-center">
                  <Link to="/generateur" className="om-btn-primary">
                    {t('prototype.notfound_cta')}
                  </Link>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="mx-auto max-w-xl text-center" data-testid="prototype-error">
                <h1 className="om-h1 text-[28px] sm:text-[36px] text-[var(--om-ink)]">
                  {t('prototype.error_title')}
                </h1>
                <p className="mt-4 text-[15px] leading-relaxed text-[var(--om-muted)]">
                  {t('prototype.error_subtitle')}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
